import React from "react";
import AccountCircleIcon from "@mui/icons-material/Person";
import { orderLeadership, sliceLeadershipRows } from "../../utils/leadershipLayout";
import "../Dashboard/Dashboard.scss";

// The leadership screen ("Our Chiefs & Officers"), exactly as the legacy board
// draws it: four themes (Classic Red with the agency's logo and name, or the
// Classic Blue / Modern Dark / Modern Light hierarchy), members ordered by
// their saved order or rank, and placed in the row an admin arranged them in.
// Shared by the legacy dashboard and the Studio's Leadership widget.
//
//   leadership  [{ id, title, name, image, level?, order?, row? }]
//   theme       "classicRed" | "classicBlue" | "modernDark" | "modernLight"
//   agencyLogo / agencyName / agencyDescription / logoAlt  Classic Red branding
export default function LeadershipBoard({
  leadership,
  theme: themeProp,
  agencyLogo,
  agencyName,
  agencyDescription,
  logoAlt,
}) {
  const theme = themeProp || 'classicRed';
  // Ordering and row grouping live in a shared helper kept in lock-step
  // with the admin dashboard, so what an admin arranges is what shows
  // here. Each member carries the `row` it was placed in; members saved
  // before rows became explicit fall back to the old index slices.
  const sortedLeadership = orderLeadership(leadership || []);
  const leadershipRows = sliceLeadershipRows(sortedLeadership, theme);
  // Flat, row-major position of each row's first card — only used to
  // build stable React keys for members with no id.
  const rowOffsets = [];
  leadershipRows.reduce((offset, row) => {
    rowOffsets.push(offset);
    return offset + row.items.length;
  }, 0);

  // Classic Red theme: logo + dept name on left, photo grid on right
  if (theme === 'classicRed') {
    const renderPortrait = (leader, index) => (
      <div key={leader.id || index} className="lt-red-portrait">
        <div className="lt-red-photo">
          {leader.image ? (
            <img
              src={leader.image}
              alt={leader.title}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className="lt-red-placeholder" style={{ display: leader.image ? 'none' : 'flex' }}>
            <svg viewBox="0 0 100 110" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="50" cy="35" r="22" fill="currentColor" />
              <path d="M10 110 C 10 75, 30 65, 50 65 C 70 65, 90 75, 90 110 Z" fill="currentColor" />
            </svg>
          </div>
        </div>
        <div className="lt-red-caption">
          <div className="lt-red-title">{leader.title}</div>
          {leader.name && <div className="lt-red-name">{leader.name}</div>}
        </div>
      </div>
    );

    // Four tiers: 3 across, 3 across, 3 across, then the rest. Who lands
    // where comes from each member's saved `row` (rank order only decides
    // it for rosters arranged before rows were explicit), so a tier may be
    // deliberately left empty or short — empty tiers are simply skipped.

    // Responsive sizing inputs for the SCSS (no resize listener — these are
    // pure render-time numbers derived from the roster size). We cap how many
    // portraits go on ONE line of a tier so each card stays a sensible
    // fraction of the track (>= ~8.5% wide => at most 9 per line), then count
    // the resulting visual rows. The SCSS divides the container's width by
    // --lt-cols and its height by --lt-rows to pick a portrait size that fits
    // BOTH axes on one screen. See .lt-red-grid in Dashboard.scss.
    const MAX_PER_LINE = 9;
    // The three fixed tiers are sized for at least 3 across so a lone chief
    // keeps the elegant fixed-card size instead of stretching the track; the
    // overflow tier sizes to its own occupancy so a long bench of lieutenants
    // shrinks without dragging the chiefs down with it.
    const redTiers = leadershipRows.map((row, index) => {
      const count = row.items.length;
      const minCols = index < 3 ? 3 : 1;
      const cols = count > 0 ? Math.max(minCols, Math.min(MAX_PER_LINE, count)) : 0;
      return {
        ...row,
        cols,
        lines: cols > 0 ? Math.ceil(count / cols) : 0,
      };
    });
    const ltRows = redTiers.reduce((sum, tier) => sum + tier.lines, 0);
    const ltGridStyle = {
      '--lt-cols': Math.max(...redTiers.map((t) => t.cols), 1),
      '--lt-rows': Math.max(ltRows, 1),
    };

    return (
      <div className="lt-red-container">
        <div className="lt-red-stage">
          <div className="lt-red-brand">
            <h1 className="lt-red-heading">Our Chiefs &amp; Officers</h1>
            <div className="lt-red-brand-body">
              {agencyLogo && (
                <img src={agencyLogo} alt={logoAlt || 'Agency Logo'} className="lt-red-logo" />
              )}
              {agencyName && (
                <div className="lt-red-dept-name">{agencyName}</div>
              )}
              {agencyDescription && (
                <div className="lt-red-tagline">{agencyDescription}</div>
              )}
            </div>
          </div>
          <div className="lt-red-grid" style={ltGridStyle}>
            {redTiers.map((tier) =>
              tier.items.length > 0 ? (
                <div
                  key={tier.index}
                  className={`lt-red-row lt-red-row-${tier.index} ${
                    tier.index === 3 ? 'lt-red-row-bot' : ''
                  }`.trim()}
                  style={{ '--lt-cols': tier.cols }}
                >
                  {tier.items.map((leader, i) =>
                    renderPortrait(leader, rowOffsets[tier.index] + i)
                  )}
                </div>
              ) : null
            )}
          </div>
        </div>
      </div>
    );
  }

  // Themes that share the hierarchy structure: classicBlue, modernDark, modernLight
  const renderLeadershipCard = (leader, index, size = 'normal') => (
    <div key={leader.id || index} className={`leadership-card ${size}`}>
      <div className="leadership-image-container">
        {leader.image ? (
          <img
            src={leader.image}
            alt={leader.title}
            className="leadership-image"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="leadership-placeholder" style={{ display: leader.image ? 'none' : 'flex' }}>
          <AccountCircleIcon style={{ fontSize: size === 'large' ? '90px' : size === 'medium' ? '75px' : '65px', color: '#ccc' }} />
        </div>
      </div>
      <div className="leadership-info">
        <h3 className="leadership-title">{leader.title}</h3>
        {leader.name && (
          <p className="leadership-name">{leader.name}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className={`dashboardColumn full-width-column leadership-container leadership-theme-${theme}`}>
      <h2>Department Leadership</h2>
      <div className="leadership-content">
        <div className="leadership-hierarchy">
          {/* Four tiers — large, medium, then two normal. Membership comes
              from each member's saved `row`, so a tier can be deliberately
              left empty or short; empty tiers are skipped so the screen
              stays tight. */}
          {leadershipRows.map((row) =>
            row.items.length > 0 ? (
              <div key={row.index} className={`leadership-row row-${row.index + 1}`}>
                {row.items.map((leader, i) =>
                  renderLeadershipCard(leader, rowOffsets[row.index] + i, row.size)
                )}
              </div>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
}
